/**
 * Utils to hide and unhide elements, by adjusting their opacity.
 *
 * It can be necessary to momentarily hide nodes or edges while calculating a flowchart's layout, to prevent
 * 'flickering' as the elements are re-positioned.
 */

import {CSSProperties} from 'react';

const HIDDEN_OPACITY_VALUE = '0%';
const VISIBLE_OPACITY_VALUE = '100%';

type ElementWithStyles = object & {style?: CSSProperties};

/**
 * Hide a flowchart node or edge, to prevent it from appearing until its layout has been calculated.
 *
 * This util hides the element by setting its opacity to "0%".
 *
 * The element will still be accounted for in layouts, but will not be visible.
 */
export function hideFlowchartElementBeforeLayout<T extends ElementWithStyles>(element: T): T {
  return {...element, style: {...element.style, opacity: HIDDEN_OPACITY_VALUE}};
}

/**
 * Unhide a flowchart node or edge, which may have been hidden by `hideElement`,
 *
 * This util unhides the element by setting its opacity to "100%". The element will not be modified if its
 * opacity value is not exactly "0%".
 */
export function unhideFlowchartElementAfterLayout<T extends ElementWithStyles>(element: T): T {
  return element.style?.opacity === HIDDEN_OPACITY_VALUE
    ? {
        ...element,
        style: {...element.style, opacity: VISIBLE_OPACITY_VALUE},
      }
    : element;
}
